/**
 * Meta Graph API service layer
 * Handles Instagram & Facebook publishing, engagement tracking, and DM automation
 */

// Read env vars at request time (inside functions) to avoid stale/undefined values
// caused by module-level caching in Next.js server components.
const GRAPH_BASE = 'https://graph.facebook.com/v20.0';

// Helper to get env vars at call time with clear error messages
function getIgToken(): string {
  const token = process.env.IG_ACCESS_TOKEN;
  if (!token) throw new Error('IG_ACCESS_TOKEN is not set in environment variables');
  return token;
}
function getIgAccountId(): string {
  const id = process.env.IG_BUSINESS_ACCOUNT_ID;
  if (!id) throw new Error('IG_BUSINESS_ACCOUNT_ID is not set in environment variables');
  return id;
}
function getFbPageId(): string {
  const id = process.env.FB_PAGE_ID;
  if (!id) throw new Error('FB_PAGE_ID is not set in environment variables');
  return id;
}
function getFbToken(): string {
  const token = process.env.FB_PAGE_ACCESS_TOKEN;
  if (!token) throw new Error('FB_PAGE_ACCESS_TOKEN is not set in environment variables');
  return token;
}
function getMetaAppId(): string {
  const id = process.env.META_APP_ID;
  if (!id) throw new Error('META_APP_ID is not set in environment variables');
  return id;
}
function getMetaAppSecret(): string {
  const secret = process.env.META_APP_SECRET;
  if (!secret) throw new Error('META_APP_SECRET is not set in environment variables');
  return secret;
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ReelPublishPayload {
  videoUrl: string;
  caption: string;
  coverImageUrl?: string;
  shareToFeed?: boolean;
}

export interface ReelEngagement {
  reelId: string;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  saves: number;
  reach: number;
  plays: number;
  timestamp: string;
}

export interface DmRule {
  id: string;
  keyword: string;
  affiliateLink: string;
  message: string;
  active: boolean;
}

export interface MonetizationMetrics {
  estimatedEarnings: number;
  reelsPlays: number;
  qualifiedPlays: number;
  monetizationStatus: 'ACTIVE' | 'PENDING' | 'NOT_ELIGIBLE';
  bonusEarnings: number;
  affiliateClicks: number;
  affiliateConversions: number;
  dmsSent: number;
  conversionRate: number;
}

// ─── Instagram Publishing ─────────────────────────────────────────────────────

/**
 * Returns the Unix timestamp (seconds) for the next upcoming 9:30 AM IST (UTC+5:30).
 * If today's 9:30 AM IST has already passed (or is less than 10 min away), schedules for tomorrow.
 * Instagram requires scheduled_publish_time to be at least 10 minutes in the future.
 */
export function getNextPublishTime9AMIST(): number {
  const now = new Date();
  // IST offset: UTC+5:30 = 330 minutes
  const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

  // Get current time in IST
  const nowIst = new Date(now.getTime() + IST_OFFSET_MS);

  // Build today's 9:30 AM IST as UTC
  const todayIst = new Date(Date.UTC(
    nowIst.getUTCFullYear(),
    nowIst.getUTCMonth(),
    nowIst.getUTCDate(),
    9, 30, 0, 0
  ));
  // Convert IST time to UTC by subtracting IST offset
  const todayPublishUtc = new Date(todayIst.getTime() - IST_OFFSET_MS);

  // If today's 9:30 AM IST is more than 10 minutes in the future, use it
  const tenMinutesMs = 10 * 60 * 1000;
  if (todayPublishUtc.getTime() - now.getTime() > tenMinutesMs) {
    return Math.floor(todayPublishUtc.getTime() / 1000);
  }

  // Otherwise schedule for tomorrow's 9:30 AM IST
  const tomorrowPublishUtc = new Date(todayPublishUtc.getTime() + 24 * 60 * 60 * 1000);
  return Math.floor(tomorrowPublishUtc.getTime() / 1000);
}

/**
 * Step 1: Upload Reel container to Instagram
 * NOTE: We do NOT use scheduled_publish_time here because:
 * - Instagram rejects containers with a scheduled time in the past
 * - Scheduled publishing requires special account permissions
 * - Instead we publish immediately via media_publish after container is FINISHED
 */
export async function createIgReelContainer(payload: ReelPublishPayload): Promise<string> {
  const IG_TOKEN = getIgToken();
  const IG_ACCOUNT_ID = getIgAccountId();

  const params = new URLSearchParams({
    media_type: 'REELS',
    video_url: payload.videoUrl,
    caption: payload.caption,
    share_to_feed: String(payload.shareToFeed ?? true),
    access_token: IG_TOKEN,
  });
  if (payload.coverImageUrl) params.set('cover_url', payload.coverImageUrl);

  const res = await fetch(`${GRAPH_BASE}/${IG_ACCOUNT_ID}/media`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: params,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(`IG container error: ${data.error?.message ?? JSON.stringify(data.error)}`);
  return data.id as string;
}

/**
 * Step 2: Poll until container is FINISHED processing
 * Max 8 attempts × 3 s = 24 s — safely within serverless timeout limits
 */
export async function waitForIgContainer(
  containerId: string,
  maxAttempts = 8,
  intervalMs = 3000
): Promise<void> {
  const IG_TOKEN = getIgToken();
  for (let i = 0; i < maxAttempts; i++) {
    const res = await fetch(
      `${GRAPH_BASE}/${containerId}?fields=status_code&access_token=${IG_TOKEN}`
    );
    const data = await res.json();
    if (data.status_code === 'FINISHED') return;
    if (data.status_code === 'ERROR') {
      throw new Error(`IG container processing failed: ${data.status ?? 'unknown error'}`);
    }
    if (i < maxAttempts - 1) {
      await new Promise((r) => setTimeout(r, intervalMs));
    }
  }
  throw new Error('IG container timed out — video may still be processing. Retry in a few seconds.');
}

/**
 * Step 3: Publish the container
 */
export async function publishIgReel(containerId: string): Promise<string> {
  const IG_TOKEN = getIgToken();
  const IG_ACCOUNT_ID = getIgAccountId();
  const params = new URLSearchParams({
    creation_id: containerId,
    access_token: IG_TOKEN,
  });
  const res = await fetch(`${GRAPH_BASE}/${IG_ACCOUNT_ID}/media_publish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: params,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(`IG publish error: ${data.error?.message ?? JSON.stringify(data.error)}`);
  return data.id as string;
}

/**
 * Full pipeline: create → wait → publish
 * Reel is scheduled to publish at 9:00 AM IST via scheduled_publish_time on the container.
 */
export async function publishReelToInstagram(payload: ReelPublishPayload): Promise<string> {
  const containerId = await createIgReelContainer(payload);
  await waitForIgContainer(containerId);
  return publishIgReel(containerId);
}

// ─── Facebook Publishing ──────────────────────────────────────────────────────

export async function publishVideoToFacebook(payload: ReelPublishPayload): Promise<string> {
  const FB_TOKEN = getFbToken();
  const FB_PAGE_ID = getFbPageId();
  const params = new URLSearchParams({
    file_url: payload.videoUrl,
    description: payload.caption,
    access_token: FB_TOKEN,
  });
  const res = await fetch(`${GRAPH_BASE}/${FB_PAGE_ID}/videos`, {
    method: 'POST',
    body: params,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(`FB publish error: ${data.error?.message}`);
  return data.id as string;
}

// ─── Engagement Tracking ──────────────────────────────────────────────────────

export async function fetchReelEngagement(reelId: string): Promise<ReelEngagement> {
  const IG_TOKEN = getIgToken();
  const fields = 'like_count,comments_count,shares,saved,reach,plays,timestamp';
  const res = await fetch(
    `${GRAPH_BASE}/${reelId}?fields=${fields}&access_token=${IG_TOKEN}`
  );
  const data = await res.json();
  if (!res.ok) throw new Error(`Engagement fetch error: ${data.error?.message}`);
  return {
    reelId,
    views: data.plays ?? 0,
    likes: data.like_count ?? 0,
    comments: data.comments_count ?? 0,
    shares: data.shares?.count ?? 0,
    saves: data.saved ?? 0,
    reach: data.reach ?? 0,
    plays: data.plays ?? 0,
    timestamp: data.timestamp ?? new Date().toISOString(),
  };
}

export async function fetchAccountInsights(): Promise<{
  impressions: number;
  reach: number;
  profileViews: number;
  followerCount: number;
}> {
  const IG_TOKEN = getIgToken();
  const IG_ACCOUNT_ID = getIgAccountId();
  const fields = 'impressions,reach,profile_views,follower_count';
  const res = await fetch(
    `${GRAPH_BASE}/${IG_ACCOUNT_ID}/insights?metric=${fields}&period=day&access_token=${IG_TOKEN}`
  );
  const data = await res.json();
  if (!res.ok) throw new Error(`Insights error: ${data.error?.message}`);
  const metrics: Record<string, number> = {};
  (data.data ?? []).forEach((m: { name: string; values: { value: number }[] }) => {
    metrics[m.name] = m.values?.[0]?.value ?? 0;
  });
  return {
    impressions: metrics['impressions'] ?? 0,
    reach: metrics['reach'] ?? 0,
    profileViews: metrics['profile_views'] ?? 0,
    followerCount: metrics['follower_count'] ?? 0,
  };
}

// ─── Monetization Metrics ─────────────────────────────────────────────────────

export async function fetchMonetizationMetrics(): Promise<MonetizationMetrics> {
  const IG_TOKEN = getIgToken();
  const IG_ACCOUNT_ID = getIgAccountId();
  // Fetch creator monetization insights from Graph API
  const res = await fetch(
    `${GRAPH_BASE}/${IG_ACCOUNT_ID}?fields=creator_monetization_status&access_token=${IG_TOKEN}`
  );
  const data = await res.json();

  const status: MonetizationMetrics['monetizationStatus'] =
    data.creator_monetization_status?.status ?? 'NOT_ELIGIBLE';

  // Fetch reels play insights for monetization-qualified plays
  const playsRes = await fetch(
    `${GRAPH_BASE}/${IG_ACCOUNT_ID}/insights?metric=ig_reels_aggregated_all_plays_count,ig_reels_video_view_total_time&period=day&access_token=${IG_TOKEN}`
  );
  const playsData = await playsRes.json();
  const playsMetrics: Record<string, number> = {};
  (playsData.data ?? []).forEach((m: { name: string; values: { value: number }[] }) => {
    playsMetrics[m.name] = m.values?.[0]?.value ?? 0;
  });

  return {
    estimatedEarnings: 0, // Requires Partner API access
    reelsPlays: playsMetrics['ig_reels_aggregated_all_plays_count'] ?? 0,
    qualifiedPlays: Math.floor((playsMetrics['ig_reels_aggregated_all_plays_count'] ?? 0) * 0.72),
    monetizationStatus: status,
    bonusEarnings: 0,
    affiliateClicks: 0,
    affiliateConversions: 0,
    dmsSent: 0,
    conversionRate: 0,
  };
}

// ─── Auto-DM Affiliate Algorithm ─────────────────────────────────────────────

/**
 * Default DM rules — keyword → affiliate link trigger
 */
export const DEFAULT_DM_RULES: DmRule[] = [
  {
    id: 'rule-1',
    keyword: 'link',
    affiliateLink: 'https://your-affiliate-link.com/product1',
    message:
      "Hey! Here's the link you asked for 👉 {{affiliate_link}} — let me know if you have questions!",
    active: true,
  },
  {
    id: 'rule-2',
    keyword: 'tool',
    affiliateLink: 'https://your-affiliate-link.com/tool',
    message:
      "Here's the tool I mentioned 🔧 {{affiliate_link}} — it's been a game changer for me!",
    active: true,
  },
  {
    id: 'rule-3',
    keyword: 'course',
    affiliateLink: 'https://your-affiliate-link.com/course',
    message:
      "Here's the course I recommend 📚 {{affiliate_link}} — use code SAVE20 for a discount!",
    active: true,
  },
  {
    id: 'rule-4',
    keyword: 'price',
    affiliateLink: 'https://your-affiliate-link.com/pricing',
    message:
      "Check out the pricing here 💰 {{affiliate_link}} — there's a free tier to get started!",
    active: true,
  },
];

/**
 * Match a comment/DM text against active rules
 */
export function matchDmRule(text: string, rules: DmRule[] = DEFAULT_DM_RULES): DmRule | null {
  const lower = text.toLowerCase();
  return rules.find((r) => r.active && lower.includes(r.keyword.toLowerCase())) ?? null;
}

/**
 * Send a DM reply via Instagram Messaging API (requires instagram_manage_messages permission)
 */
export async function sendInstagramDm(
  recipientId: string,
  message: string,
  affiliateLink: string
): Promise<void> {
  const body = message.replace('{{affiliate_link}}', affiliateLink);
  const res = await fetch(`${GRAPH_BASE}/${IG_ACCOUNT_ID}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      recipient: { id: recipientId },
      message: { text: body },
      access_token: IG_TOKEN,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(`DM send error: ${data.error?.message}`);
}

/**
 * Process incoming comment webhook — auto-DM if keyword matched
 */
export async function processCommentWebhook(
  commentText: string,
  commenterId: string,
  rules: DmRule[] = DEFAULT_DM_RULES
): Promise<{ matched: boolean; rule?: DmRule }> {
  const rule = matchDmRule(commentText, rules);
  if (!rule) return { matched: false };
  await sendInstagramDm(commenterId, rule.message, rule.affiliateLink);
  return { matched: true, rule };
}
